import { ChangeDetectorRef, Component, DoCheck, OnDestroy, OnInit } from '@angular/core';
import { FieldType, FieldTypeConfig, FormlyFieldProps } from '@ngx-formly/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { firstValueFrom } from 'rxjs';
import { SearchService } from '@nx-ahs/search';
import { ExternalSyncService, VerificationSyncConfig } from '../../../core/services/external-sync.service';

export interface InlineSearchCheckboxDef {
	key: string;
	label?: string;
	defaultChecked?: boolean;
}

export interface InlineSearchColumnDef {
	key: string;
	label: string;
	format?: 'date' | 'phone' | 'ssn' | 'text';
	checkbox?: InlineSearchCheckboxDef;
}

export interface InlineSearchFieldDef {
	key: string;
	label: string;
	searchCriteriaLabel?: string;
	placeholder?: string;
	type?: 'text' | 'date';
	maxLength?: number;
}

interface InlinePersonSearchProps extends FormlyFieldProps {
	searchId?: number;
	columns?: InlineSearchColumnDef[];
	searchFields?: InlineSearchFieldDef[];
	criteriaValues?: Record<string, string>;
	/** Criteria keys filled from the call by the propertyPrefill extension; used for the "From call" hint and `autoSearch`. */
	prefilledCriteria?: string[];
	/**
	 * Runs the search when the component starts, but only if at least one criterion was prefilled from the call.
	 * Never selects a row or ticks a verification checkbox.
	 */
	autoSearch?: boolean;
	noResultsMessage?: string;
	patchSiblings?: boolean;
	requiredVerifications?: number;
	pageSize?: number;
	/**
	 * Refreshes the selected row from an external system when it is selected, before it can be verified.
	 * Only active when the ExternalDataSync feature flag is on; omit to never sync (e.g. provider search).
	 */
	verificationSync?: VerificationSyncConfig;
}

export const DEFAULT_INLINE_SEARCH_PAGE_SIZE = 5;
export const DEFAULT_VERIFICATION_SYNC_WARNING =
	'Member data could not be refreshed. The information shown may be out of date.';

@Component({
	selector: 'nx-ahs-formly-inline-person-search',
	templateUrl: './inline-person-search.component.html',
	styleUrl: './inline-person-search.component.css',
	standalone: false
})
export class InlinePersonSearchComponent
	extends FieldType<FieldTypeConfig<InlinePersonSearchProps>>
	implements OnInit, DoCheck, OnDestroy
{
	public searchForm!: FormGroup;
	public results: Record<string, unknown>[] = [];
	public selectedIndex: number | null = null;
	public checkboxStates: Record<number, Record<string, boolean>> = {};
	public isLoading = false;
	public hasSearched = false;
	public sortKey: string | null = null;
	public sortAsc = true;
	public sortedResults: { row: Record<string, unknown>; originalIndex: number }[] = [];
	public pageIndex = 0;
	/** True while the selected row is being refreshed from the external system; verification is blocked. */
	public isSyncing = false;
	/** Set when the last refresh failed, so the agent knows the displayed row may be stale. */
	public syncWarning: string | null = null;

	// Outcome per original row index for the current result set, so re-selecting a row never re-syncs it.
	private syncStatus: Record<number, 'synced' | 'failed'> = {};
	// Incremented whenever the selection or result set changes; a sync response for an older value is discarded.
	private syncToken = 0;
	// requiredVerifications as of the last time it was written to the control value; undefined until ngOnInit.
	private lastEmittedRequired?: number;

	/** Rows per page. Set via `props.pageSize`; falls back to 5 when missing or not a positive integer. */
	public get pageSize(): number {
		const size = Number(this.props.pageSize);
		return Number.isInteger(size) && size > 0 ? size : DEFAULT_INLINE_SEARCH_PAGE_SIZE;
	}

	/** The slice of sortedResults shown on the current page. originalIndex is preserved, so selection survives paging. */
	public get pagedResults(): { row: Record<string, unknown>; originalIndex: number }[] {
		const start = this.pageIndex * this.pageSize;
		return this.sortedResults.slice(start, start + this.pageSize);
	}

	public onPageChange(event: PageEvent): void {
		this.pageIndex = event.pageIndex;
	}

	public get isReadonly(): boolean {
		return !!(this.formState?.disabled || this.props.disabled);
	}

	public get columns(): InlineSearchColumnDef[] {
		return this.props.columns ?? [];
	}

	public get searchFields(): InlineSearchFieldDef[] {
		return this.props.searchFields ?? [{ key: 'tokens', label: 'Search', placeholder: 'Type to search…' }];
	}

	public get searchId(): number {
		return this.props.searchId ?? 3;
	}

	public get noResultsMessage(): string {
		return this.props.noResultsMessage ?? 'No results found.';
	}

	public get totalCheckboxCount(): number {
		return this.columnsWithCheckboxes.length;
	}

	public get requiredVerifications(): number {
		return this.props.requiredVerifications ?? this.totalCheckboxCount;
	}

	public get verifiedCount(): number {
		if (this.selectedIndex === null) return 0;
		return Object.values(this.checkboxStates[this.selectedIndex] ?? {}).filter(Boolean).length;
	}

	public get isVerificationMet(): boolean {
		return this.selectedIndex !== null && !this.isSyncing && this.verifiedCount >= this.requiredVerifications;
	}

	public toggleSort(key: string): void {
		if (this.sortKey === key) {
			this.sortAsc = !this.sortAsc;
		} else {
			this.sortKey = key;
			this.sortAsc = true;
		}
		this.rebuildSortedResults();
	}

	private rebuildSortedResults(): void {
		this.pageIndex = 0;
		const indexed = this.results.map((row, i) => ({ row, originalIndex: i }));
		if (!this.sortKey) {
			this.sortedResults = indexed;
			return;
		}
		const key = this.sortKey;
		this.sortedResults = [...indexed].sort((a, b) => {
			const av = a.row[key] ?? '';
			const bv = b.row[key] ?? '';
			const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
			return this.sortAsc ? cmp : -cmp;
		});
	}

	private get columnsWithCheckboxes(): InlineSearchColumnDef[] {
		return this.columns.filter((col) => !!col.checkbox);
	}

	constructor(
		private formBuilder: FormBuilder,
		private searchService: SearchService,
		private externalSyncService: ExternalSyncService,
		private cdr: ChangeDetectorRef
	) {
		super();
	}

	ngOnInit(): void {
		const controls: Record<string, FormControl> = {};
		const prefill = this.props.criteriaValues ?? {};
		this.searchFields.forEach((f) => {
			controls[f.key] = new FormControl(prefill[f.key] ?? '');
		});
		this.searchForm = this.formBuilder.group(controls);

		if (this.isReadonly) {
			this.searchForm.disable();
		}

		this.lastEmittedRequired = this.requiredVerifications;
		this.formControl.setValue({
			selectedRow: null,
			verifiedCount: 0,
			requiredVerifications: this.requiredVerifications
		});
		this.formControl.setErrors({ verificationRequired: true });

		if (this.props.autoSearch && this.prefilledKeys.length > 0) {
			void this.onSearch();
		}
	}

	/** True while a criterion still holds the value the call supplied, so the hint disappears once the agent edits it. */
	public isFromCall(key: string): boolean {
		const value = this.props.criteriaValues?.[key];
		return this.prefilledKeys.includes(key) && this.searchForm?.get(key)?.value === value;
	}

	private get prefilledKeys(): string[] {
		return this.props.prefilledCriteria ?? [];
	}

	ngDoCheck(): void {
		// requiredVerifications can change after init (e.g. a nested-script propOverrides rule re-applied it). Re-emit so
		// the control value, validity, and anything interpolating selectedMember.requiredVerifications stay current.
		const required = this.requiredVerifications;
		if (this.lastEmittedRequired !== undefined && this.lastEmittedRequired !== required) {
			this.lastEmittedRequired = required;
			void Promise.resolve().then(() => {
				this.emitValue();
				this.cdr.markForCheck();
			});
		}
	}

	ngOnDestroy(): void {
		// Discard any in-flight sync response; the field no longer exists to receive it.
		this.syncToken++;
	}

	public async onSearch(): Promise<void> {
		if (this.isReadonly || this.isLoading) return;

		this.isLoading = true;
		this.results = [];
		this.selectedIndex = null;
		this.checkboxStates = {};
		this.resetSyncState();
		this.formControl.setValue(null);

		try {
			const results = await firstValueFrom(
				this.searchService.search(this.searchId, this.searchForm.value, false)
			);

			this.results = (results as Record<string, unknown>[]) ?? [];
			this.hasSearched = true;

			this.results.forEach((_, i) => {
				this.checkboxStates[i] = {};
				this.columnsWithCheckboxes.forEach((col) => {
					this.checkboxStates[i][col.checkbox!.key] = col.checkbox!.defaultChecked ?? false;
				});
			});

			this.rebuildSortedResults();
		} finally {
			this.isLoading = false;
			this.cdr.markForCheck();
		}
	}

	public selectRow(index: number): void {
		if (this.isReadonly) return;
		// Clicking the row that is already being refreshed must not restart or cancel its sync.
		if (this.isSyncing && this.selectedIndex === index) return;

		const switchingRows = this.selectedIndex !== index;
		if (this.selectedIndex !== null && switchingRows) {
			this.resetCheckboxStates(this.selectedIndex);
		}
		if (switchingRows) {
			// Abandon any in-flight sync for the previously selected row.
			this.syncToken++;
			this.isSyncing = false;
			this.syncWarning = this.syncStatus[index] === 'failed' ? this.syncWarningMessage : null;
		}
		this.selectedIndex = index;

		if (this.shouldSync(index)) {
			void this.syncSelectedRow(index);
			return;
		}
		this.emitValue();
	}

	public onCheckboxChange(): void {
		this.emitValue();
	}

	public clearSearch(): void {
		this.searchForm.reset();
		this.results = [];
		this.sortedResults = [];
		this.pageIndex = 0;
		this.sortKey = null;
		this.sortAsc = true;
		this.selectedIndex = null;
		this.checkboxStates = {};
		this.resetSyncState();
		this.hasSearched = false;
		this.formControl.setValue({
			selectedRow: null,
			verifiedCount: 0,
			requiredVerifications: this.requiredVerifications
		});
		this.formControl.setErrors({ verificationRequired: true });
	}

	private get syncWarningMessage(): string {
		return this.props.verificationSync?.warningMessage ?? DEFAULT_VERIFICATION_SYNC_WARNING;
	}

	private shouldSync(index: number): boolean {
		return (
			this.syncStatus[index] === undefined &&
			this.externalSyncService.isVerificationSyncAvailable(this.props.verificationSync)
		);
	}

	private resetSyncState(): void {
		this.syncToken++;
		this.isSyncing = false;
		this.syncWarning = null;
		this.syncStatus = {};
	}

	private resetCheckboxStates(index: number): void {
		this.checkboxStates[index] = {};
		this.columnsWithCheckboxes.forEach((col) => {
			this.checkboxStates[index][col.checkbox!.key] = col.checkbox!.defaultChecked ?? false;
		});
	}

	/**
	 * Refreshes the selected row from the external system. Verification stays blocked until it finishes
	 * (the control is invalid with `syncPending`); on failure the agent is warned and may still verify the
	 * row as it was. A response that arrives after the selection changed is discarded.
	 */
	private async syncSelectedRow(index: number): Promise<void> {
		const token = ++this.syncToken;
		const row = this.results[index];

		this.isSyncing = true;
		this.syncWarning = null;
		this.emitValue();

		const result = await this.externalSyncService.syncVerificationRow(this.props.verificationSync!, row);
		if (token !== this.syncToken) return;

		this.isSyncing = false;
		if (result.ok === true) {
			// Merge in place: sortedResults holds references to these row objects, and rebuilding it would reset paging.
			Object.assign(row, result.row);
			this.syncStatus[index] = 'synced';
			// The caller has not been able to tick anything yet, and the values they would verify just changed.
			this.resetCheckboxStates(index);
		} else {
			this.syncStatus[index] = 'failed';
			if (result.reason !== 'disabled') {
				this.syncWarning = this.syncWarningMessage;
			}
		}

		this.cdr.markForCheck();
		this.emitValue();
	}

	/** Accessible name of a column's verification checkbox: the configured label, else "Verify <column label>". */
	public checkboxAriaLabel(col: InlineSearchColumnDef): string {
		return col.checkbox?.label || `Verify ${col.label}`;
	}

	public formatCell(row: Record<string, unknown>, col: InlineSearchColumnDef): string {
		const value = row[col.key];
		if (value == null) return '';

		switch (col.format) {
			case 'date': {
				const d = new Date(value as string);
				return isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
			}
			case 'phone': {
				const digits = String(value).replace(/\D/g, '');
				const m = digits.match(/^(\d{3})(\d{3})(\d{4})$/);
				return m ? `(${m[1]}) ${m[2]}-${m[3]}` : String(value);
			}
			case 'ssn': {
				const digits = String(value).replace(/\D/g, '');
				return digits.length === 9 ? `***-**-${digits.slice(-4)}` : String(value);
			}
			default:
				return String(value);
		}
	}

	private emitValue(): void {
		if (this.selectedIndex === null) {
			this.updateValidation();
			this.formControl.setValue({
				selectedRow: null,
				verifiedCount: 0,
				requiredVerifications: this.requiredVerifications
			});
			return;
		}

		const selectedRow = this.results[this.selectedIndex];
		const value = {
			selectedRow,
			// Scripts gate completion on this count (e.g. a hideExpression on a _complete field), so nothing counts as
			// verified while the row's data is still being refreshed.
			verifiedCount: this.isSyncing ? 0 : this.verifiedCount,
			requiredVerifications: this.requiredVerifications
		};

		// updateValidation must run before setValue so that when setValue fires
		// valueChanges (triggering the nested-script rollup), the form is already
		// in the correct valid/invalid state for the allFormsValid check.
		this.updateValidation();
		this.formControl.setValue(value);

		if (this.props.patchSiblings) {
			this.field.parent?.formControl?.patchValue(selectedRow);
		}
	}

	private updateValidation(): void {
		if (this.isVerificationMet) {
			this.formControl.setErrors(null);
		} else {
			this.formControl.setErrors({ verificationRequired: true });
		}
		this.formControl.markAsTouched();
	}
}
